import React from "react";
import { Switch as AriaSwitch, SwitchProps as AriaSwitchProps } from "react-aria-components";
import { composeTailwindRenderProps, focusRing } from "../../utils";
import type { Size } from "../../utils/controlStyles";

export interface SwitchProps extends Omit<AriaSwitchProps, "children"> {
  children: React.ReactNode;
  /**
   * Visual variant of the switch
   * @default "default"
   */
  variant?: "default" | "brand";
  /**
   * Size of the switch
   * @default "md"
   */
  size?: Size;
}

// Size configurations for track and handle
const sizeConfig = {
  sm: {
    track: "h-4 w-7",
    handle: "h-3.5 w-3.5",
    text: "text-xs",
  },
  md: {
    track: "h-5 w-9",
    handle: "h-4 w-4",
    text: "text-sm",
  },
  lg: {
    track: "h-6 w-11",
    handle: "h-5 w-5",
    text: "text-base",
  },
  xl: {
    track: "h-7 w-14",
    handle: "h-6 w-6",
    text: "text-lg",
  },
} as const;

function getTrackClassName({
  isSelected,
  isDisabled,
  variant = "default",
  size = "md",
}: {
  isSelected: boolean;
  isDisabled: boolean;
  variant?: "default" | "brand";
  size?: Size;
}) {
  const baseClasses =
    "flex px-px items-center shrink-0 cursor-default rounded-full transition duration-200 ease-in-out shadow-inner border border-transparent";
  const sizeClasses = sizeConfig[size].track;
  const focusRingClasses = focusRing;
  const focusRingColor =
    variant === "brand" ? "focus-visible:outline-action-brand" : "focus-visible:outline-action-default";

  let selectedClasses = "";
  if (isSelected) {
    if (variant === "brand") {
      selectedClasses = "bg-action-brand forced-colors:!bg-[Highlight] group-pressed:bg-action-brand-hover";
    } else {
      selectedClasses = "bg-action-default forced-colors:!bg-[Highlight] group-pressed:bg-action-default-hover";
    }
  } else {
    selectedClasses = "bg-gray-400 dark:bg-zinc-400 group-pressed:bg-gray-500 dark:group-pressed:bg-zinc-300";
  }

  const disabledClasses = isDisabled
    ? "bg-gray-200 dark:bg-zinc-700 forced-colors:group-selected:!bg-[GrayText] forced-colors:border-[GrayText]"
    : "";

  return [baseClasses, sizeClasses, focusRingClasses, focusRingColor, selectedClasses, disabledClasses]
    .filter(Boolean)
    .join(" ");
}

function getHandleClassName({
  isSelected,
  isDisabled,
  size = "md",
}: {
  isSelected: boolean;
  isDisabled: boolean;
  size?: Size;
}) {
  const baseClasses =
    "transform rounded-full bg-white dark:bg-zinc-900 outline-none shadow transition duration-200 ease-in-out";
  const sizeClasses = sizeConfig[size].handle;
  const selectedClasses = isSelected ? "translate-x-[100%]" : "translate-x-0";
  const disabledClasses = isDisabled ? "forced-colors:outline-[GrayText]" : "";

  return [baseClasses, sizeClasses, selectedClasses, disabledClasses].filter(Boolean).join(" ");
}

/**
 * Switch
 *
 * Toggle switch component for binary on/off states.
 * Provides an accessible alternative to checkboxes for settings and preferences.
 */
export function Switch({ children, variant = "default", size = "md", ...props }: SwitchProps) {
  return (
    <AriaSwitch
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        `group flex items-center gap-2 ${sizeConfig[size].text} text-gray-800 transition disabled:text-gray-300 dark:text-zinc-200 dark:disabled:text-zinc-600 forced-colors:disabled:text-[GrayText]`
      )}
    >
      {(renderProps) => (
        <>
          <div className={getTrackClassName({ ...renderProps, variant, size })}>
            <span className={getHandleClassName({ ...renderProps, size })} />
          </div>
          {children}
        </>
      )}
    </AriaSwitch>
  );
}
